import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ModalMC from './ModalMC';
import TitleMC from './TitleMC';
import IconMC from './IconMC';
import ButtonMC from './ButtonMC';
import { formatReminderOffsetLabel } from '../../components/shared/CalendarEventReminderSelector';
import { Z_INDEX } from '../../constants/zIndex';

const formatDateTime = (dateValue, language) => {
  if (!dateValue) return '-';

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return '-';

  const locale = language?.startsWith('en') ? 'en-US' : 'es-ES';
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(parsedDate);
};

const CalendarEventShowModalMC = ({
  isOpen,
  event = null,
  onClose,
  onEdit,
  onDelete
}) => {
  const { t, i18n } = useTranslation('CalendarEventsScreenMC');

  const startsAtLabel = useMemo(() => formatDateTime(event?.start, i18n.language), [event?.start, i18n.language]);
  const endsAtLabel = useMemo(() => formatDateTime(event?.end, i18n.language), [event?.end, i18n.language]);
  const reminders = event?.reminders || [];

  if (!event) return null;

  return (
    <ModalMC
      visible={isOpen}
      onClose={onClose}
      hideCloseButton
      zIndex={Z_INDEX.calendarModal}
      containerClassName="tw-w-[95vw] tw-max-w-[520px]"
      className="!tw-p-0"
      customHeader={(
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pt-5 tw-pb-4 tw-border-b tw-border-gray-100">
          <TitleMC
            text={event.title || t('title')}
            textClassName="tw-text-lg tw-font-semibold tw-text-gray-900"
          />
          <button
            type="button"
            onClick={onClose}
            className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-600"
            aria-label={t('actions.close')}
          >
            <IconMC name="close-outline" />
          </button>
        </div>
      )}
    >
      <div className="tw-px-6 tw-py-6 tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-gray-500">
            {t('show.title_starts_at')}
          </p>
          <p className="tw-mb-0 tw-text-sm tw-text-gray-900">{startsAtLabel}</p>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-1">
          <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-gray-500">
            {t('show.title_ends_at')}
          </p>
          <p className="tw-mb-0 tw-text-sm tw-text-gray-900">{endsAtLabel}</p>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2">
          <p className="tw-mb-0 tw-text-xs tw-font-semibold tw-text-gray-500">
            {t('show.title_remember')}
          </p>
          {reminders.length === 0 ? (
            <p className="tw-mb-0 tw-text-sm tw-text-gray-900">
              {t('show.no_remember', { defaultValue: 'Sin recordatorios' })}
            </p>
          ) : (
            <ul className="tw-mb-0 tw-pl-0 tw-list-none tw-flex tw-flex-col tw-gap-2">
              {reminders.map((reminder) => (
                <li
                  key={reminder.id || reminder.offset_minutes}
                  className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-900"
                >
                  <span>{formatReminderOffsetLabel(reminder.offset_minutes)}</span>
                  {reminder.delivered_at && (
                    <span className="tw-rounded-full tw-bg-green-50 tw-text-green-700 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5">
                      {t('show.remember_sent', { defaultValue: 'Enviado' })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-gray-100">
        <ButtonMC
          text={t('actions.close')}
          variant="GRAY"
          onClick={onClose}
          size="medium"
        />
        <ButtonMC
          text={t('actions.delete')}
          variant="DANGER"
          outlined
          onClick={() => onDelete?.(event.id)}
          size="medium"
        />
        <ButtonMC
          text={t('quick_view.edit')}
          variant="PRIMARY"
          onClick={() => onEdit?.(event)}
          size="medium"
        />
      </div>
    </ModalMC>
  );
};

export default CalendarEventShowModalMC;
